
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useUpdateClass } from '@/api/classes';
import type { GymClass } from '@/api/classes';
import { StaffSelector } from './staff-selector';
import { DaysOfWeekSelector } from './days-of-week-selector';
import { BlackoutDatesManager } from './blackout-dates-manager';

const classSchema = z.object({
  name: z.string().min(1, 'Class name is required'),
  label: z.string().min(1, 'Class label is required'),
  isActive: z.boolean(),
  staffMember: z.string().optional(),
  daysOfWeek: z.array(z.number()).optional(),
});

type ClassFormData = z.infer<typeof classSchema>;

interface EditClassDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymClass: GymClass;
}

export function EditClassDialog({
  open,
  onOpenChange,
  gymClass,
}: EditClassDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const updateClassMutation = useUpdateClass();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ClassFormData>({
    resolver: zodResolver(classSchema),
    defaultValues: {
      name: gymClass.name,
      label: gymClass.label,
      isActive: gymClass.isActive,
      staffMember: gymClass.staffMember || '',
      daysOfWeek: gymClass.daysOfWeek || [],
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: gymClass.name,
        label: gymClass.label,
        isActive: gymClass.isActive,
        staffMember: gymClass.staffMember || '',
        daysOfWeek: gymClass.daysOfWeek || [],
      });
    }
  }, [open, gymClass, reset]);

  const staffMember = watch('staffMember');
  const daysOfWeek = watch('daysOfWeek');
  const isActive = watch('isActive');

  const onSubmit = async (data: ClassFormData) => {
    try {
      const payload: any = {
        name: data.name,
        label: data.label,
        isActive: data.isActive,
      };

      if (data.staffMember) {
        payload.staffMember = data.staffMember;
      } else {
        payload.staffMember = null;
      }

      if (data.daysOfWeek && data.daysOfWeek.length > 0) {
        payload.daysOfWeek = data.daysOfWeek;
      } else {
        payload.daysOfWeek = [];
      }

      await updateClassMutation.mutateAsync({
        classId: gymClass._id,
        data: payload,
      });

      toast({
        title: t('common.success'),
        description: t('classes.classUpdated', 'Class updated successfully'),
      });
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.message || t('common.errorOccurred'),
        variant: 'destructive',
      });
    }
  };

  const handleAddBlackoutDate = async (date: string) => {
    try {
      await updateClassMutation.mutateAsync({
        classId: gymClass._id,
        data: { addBlackoutDate: date },
      });
      toast({
        title: t('common.success'),
        description: t('classes.blackoutDateAdded', 'Blackout date added'),
      });
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.message || t('common.errorOccurred'),
        variant: 'destructive',
      });
    }
  };

  const handleRemoveBlackoutDate = async (date: string) => {
    try {
      await updateClassMutation.mutateAsync({
        classId: gymClass._id,
        data: { removeBlackoutDate: date },
      });
      toast({
        title: t('common.success'),
        description: t('classes.blackoutDateRemoved', 'Blackout date removed'),
      });
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.message || t('common.errorOccurred'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('classes.editClass', 'Edit Class')}</DialogTitle>
          <DialogDescription>
            {t('classes.editClassDescription', 'Update class settings and schedule')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('classes.className')}</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder={t('classes.className')}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="label">{t('classes.classLabel')}</Label>
            <Input
              id="label"
              {...register('label')}
              placeholder={t('classes.classLabel')}
            />
            {errors.label && (
              <p className="text-sm text-destructive">{errors.label.message}</p>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="isActive"
              checked={isActive}
              onCheckedChange={(checked) => setValue('isActive', checked)}
            />
            <Label htmlFor="isActive" className="cursor-pointer">
              {t('classes.active')}
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="staffMember">
              {t('classes.defaultInstructor', 'Default Instructor')} ({t('common.optional', 'Optional')})
            </Label>
            <StaffSelector
              value={staffMember || ''}
              onChange={(value) => setValue('staffMember', value)}
              allowClear
              placeholder={t('classes.selectInstructor', 'Select instructor...')}
            />
            <p className="text-sm text-muted-foreground">
              {t('classes.defaultInstructorHint', 'This instructor will be assigned to all timeslots by default')}
            </p>
          </div>

          <div className="space-y-2">
            <Label>
              {t('classes.classSchedule', 'Class Schedule')} ({t('common.optional', 'Optional')})
            </Label>
            <DaysOfWeekSelector
              value={daysOfWeek || []}
              onChange={(days) => setValue('daysOfWeek', days)}
            />
            <p className="text-sm text-muted-foreground">
              {t('classes.classScheduleHint', 'If empty, uses timeslot-level settings')}
            </p>
          </div>

          <div className="space-y-2">
            <Label>
              {t('classes.blackoutDates', 'Blackout Dates')}
            </Label>
            <BlackoutDatesManager
              dates={gymClass.blackoutDates || []}
              onAdd={handleAddBlackoutDate}
              onRemove={handleRemoveBlackoutDate}
              disabled={updateClassMutation.isPending}
            />
            <p className="text-sm text-muted-foreground">
              {t('classes.blackoutDatesHint', 'Class will be unavailable on these dates')}
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={updateClassMutation.isPending}>
              {updateClassMutation.isPending
                ? t('common.saving')
                : t('common.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
